import { memo, useMemo } from 'react';
import { TableCells, SortParamsArg } from 'models';
import {
  useTheme,
  useMediaQuery,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import { toUpperFirstChar } from 'utils';

import { SortIcon, ArrowIcon } from 'components';
import { useStyles } from './style';

interface Props<T> {
  tableCells: TableCells<T>[];
  orderBy: string;
  order: 'asc' | 'desc';
  handleChangeSortParams: (params: SortParamsArg) => void;
}

function TableHeader<T extends {}>({
  tableCells,
  order,
  orderBy,
  handleChangeSortParams,
}: React.PropsWithChildren<Props<T>>) {
  const theme = useTheme();
  const xs = useMediaQuery(theme.breakpoints.down(theme.size.xs));
  const small = useMediaQuery(
    theme.breakpoints.between(theme.size.xs, theme.size.sm)
  );
  const classes = useStyles();
  const { t } = useTranslation();

  const mainTableCells = useMemo(() => {
    return tableCells.filter((cell) =>
      small ? _get(cell, 'small') : xs ? _get(cell, 'xs') : true
    );
  }, [tableCells, small, xs]);

  return (
    <TableHead>
      <TableRow>
        {mainTableCells.map((cell) => {
          const sortField = cell.logicalName || cell.id;
          const isActive = orderBy === sortField;
          const disabled = cell.canSort === false;

          return (
            <TableCell
              key={sortField}
              style={small ? cell.smallStyles : xs ? cell.xsStyles : cell.style}
              classes={{ root: classes.cellRoot }}
            >
              <TableSortLabel
                classes={{
                  root: classes.tableSortRoot,
                  icon: classes.visible,
                }}
                active={isActive}
                direction={isActive ? order : 'asc'}
                disabled={disabled}
                hideSortIcon={disabled}
                onClick={() =>
                  handleChangeSortParams({ id: sortField, type: cell.type })
                }
                IconComponent={(params) => {
                  return isActive ? (
                    <ArrowIcon
                      dir={order === 'asc' ? 'top' : 'bottom'}
                      style={{ marginLeft: 7 }}
                    />
                  ) : (
                    <SortIcon {...params} style={{ marginLeft: 7 }} />
                  );
                }}
              >
                <span className={classes.overflow} title={t(cell.label)}>
                  {cell.defaultLabelRegister
                    ? t(cell.label)
                    : toUpperFirstChar(t(cell.label))}
                </span>
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}

export default memo(TableHeader);
